import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  Inject,
  PLATFORM_ID,
  ChangeDetectorRef,
  NgZone,
  inject,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
} from '@angular/core';
import { ScrollToTop } from '../../components/common/scroll-to-top/scroll-to-top';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { PostService, IPost, IPaginatedPosts } from '../../shared/services/post.service';
import { Subscription } from 'rxjs';
import { ReactionType } from '../../shared/services/event.service';
// import { ReactionSoundService } from '../../shared/services/reaction-sound.service';
import { Modal } from '../../components/ui/modal/modal';
import { RelationshipService } from '../../shared/services/relationship.service';
import { FriendRequestService, FriendshipStatus } from '../../shared/services/friend-request.service';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthStateService } from '../../shared/states/auth.state';
import { ConfirmationPopup } from '../../components/ui/confirmation-popup/confirmation-popup';
import { FavouriteService } from '../../shared/services/favourites.service';
import { ShareSheetComponent } from '../../components/ui/share-sheet/share-sheet';
import { ShareConfig } from '../../shared/services/share.service';
import { EditPostService, IEditPostRequest, IEditPostUser } from '../../shared/services/edit-post.service';
import { SearchService } from '../../shared/services/search.service';
import { FeedSnapshotService } from '../../shared/services/feed-snapshot.service';
import { LazyMediaDirective } from '../../shared/directives/lazy-media.directive';
import { SwipeDirective } from '../../shared/directives/swipe.directive';
import { FriendRequestLimitPopup } from '../../components/ui/friend-request-limit-popup/friend-request-limit-popup';
import { LinkPreviewComponent } from '../../components/ui/link-preview/link-preview';
import { ReportModal } from '../../components/ui/report-modal/report-modal';
import { ReportService } from '../../shared/services/report.service';
import { BlockUserConfirm } from '../../components/ui/block-user-confirm/block-user-confirm';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const ROW_UNIT = 10;
const ROW_GAP = 20;

// ── Reactions config ──────────────────────────────────────────────────────────

export const REACTIONS: {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
  bg: string;
}[] = [
    { type: 'LIKE', emoji: '👍', label: 'Like', color: '#2078f4', bg: '#e7f3ff' },
    { type: 'HEART', emoji: '❤️', label: 'Love', color: '#f33e58', bg: '#ffe7eb' },
    { type: 'HUG', emoji: '🤗', label: 'Hug', color: '#f7b125', bg: '#fff4e0' },
    { type: 'WOW', emoji: '😮', label: 'Wow', color: '#f7b125', bg: '#fff4e0' },
    { type: 'SAD', emoji: '😢', label: 'Sad', color: '#7c93a3', bg: '#eef2f5' },
    { type: 'HAHA', emoji: '😂', label: 'Haha', color: '#f7b125', bg: '#fff4e0' },
  ];

interface ReactionCounts {
  LIKE: number; HEART: number; HUG: number;
  WOW: number; SAD: number; HAHA: number;
}

export interface Post {
  id: string;
  hostName: string;
  hostAvatar: string;
  hostId: string;
  timeAgo: string;
  images: { url: string; type: 'image' | 'video' }[];
  rawMedia: { _id: string; url: string; urlLarge?: string; urlMedium?: string; urlSmall?: string; type: 'image' | 'video' }[];
  activeSlide: number;
  title: string;
  description: string;
  expanded: boolean;
  myReaction: ReactionType | any;
  reactions: number;
  comments: number;
  shares: number;
  isMyPost: boolean;
  isFollowed: boolean;
  totalReactions: number;
  friendshipStatus: FriendshipStatus;
  location?: string;
  hashtags: string[];
  commentsDisabled: boolean;
  backgroundPattern?: string;
  backgroundBg?: string;
  mentions: any[];
  collaborations: any[];
  hasBackground: boolean;
  showEmojiPicker: boolean;
  hoveredReaction: ReactionType | null;
  reactionCounts: ReactionCounts;
  recentUserName: string | null;
  topTypes: ReactionType[];
  likes: number;
  friendshipRequestId: string | null;
  isFavourite: boolean;
  /** Sequence index assigned by the "All" page mixed feed — used as CSS `order`
   *  so cards from posts/blogs/events interleave by createdAt across components. */
  globalOrder?: number;
  /** Masonry `grid-row` span captured when the user left for a post's detail
   *  screen, so the restored card paints at its final height straight away. */
  gridSpan?: string;
  acceptedCollaborators: Array<{
    _id: string;
    displayName: string;
    userName: string;
    profileImage?: { urlSmall?: string };
  }>;
  /** True only when this post was removed by an admin after a report — the
   *  backend only ever includes a removed post in the response when the
   *  viewer is its own author, so this flag is exclusively for the "this
   *  content was removed by admin" banner shown to the owner. */
  isRemovedByAdmin: boolean;
}

@Component({
  selector: 'app-posts',
  standalone: true,
  imports: [CommonModule, ScrollToTop, Modal, RouterModule, ConfirmationPopup, ShareSheetComponent, LazyMediaDirective, FriendRequestLimitPopup, LinkPreviewComponent, SwipeDirective, ReportModal, BlockUserConfirm],
  templateUrl: './posts.html',
  styles: [':host { display: contents; }'],
})
export class Posts implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('scrollSentinel') sentinelRef!: ElementRef<HTMLElement>;
  @ViewChildren('cardRef') cardRefs!: QueryList<ElementRef<HTMLElement>>;

  @Input() userName?: string;
  @Input() embedded = false;
  @Input() featured = false;
  /** Home page only: lay cards out in an equal-height grid instead of
   *  masonry (fixed media frame, clamped text, footer pinned to the bottom).
   *  Off everywhere else, so every other page keeps the masonry layout. */
  @Input() uniform = false;
  @Input() favouritesMode = false;
  /** When true (profile page toggle), only shows posts where `userName` is an
   *  accepted collaborator rather than the author. */
  @Input() collabOnly = false;
  @Input() gridCols = 0;
  /** When set (by the header search box or the "All" page), fetches global search
   *  results (title match) instead of the normal feed. */
  @Input() searchQuery?: string;
  /** When set (e.g. user clicked a #hashtag on a post/blog/event), fetches only
   *  posts carrying that exact hashtag. */
  @Input() searchHashtag?: string;

  /** When provided (by the "All" page mixed feed), renders these pre-fetched
   *  items instead of fetching its own — lets multiple content types be
   *  interleaved into a single shared grid while this component still owns
   *  all card rendering/interaction logic. */
  @Input() mixedItems?: any[];
  /** True when embedded in the "All" page mixed feed. Disables this
   *  component's own fetch/pagination/scroll-sentinel and lets its wrapper
   *  divs collapse (via CSS `display: contents`) so cards become direct
   *  children of the shared grid. */
  @Input() mixedMode = false;

  allPosts: Post[] = [];
  visiblePosts: Post[] = [];
  isLoading = false;
  /** True while a saved list is being restored (see restoreSnapshot). */
  isRestoring = false;
  allLoaded = false;
  currentPage = 1;
  totalPosts = 0;

  private scrollObserver?: IntersectionObserver;
  private resizeObserver?: ResizeObserver;
  private cardChangeSub?: any;
  private refreshSub?: Subscription;
  private relationshipSub = new Subscription();
  readonly REACTIONS = REACTIONS;

  isReactionOpen = false;
  reactionData: any = null;
  reactionsLoading = false;
  activeReactionFilter: ReactionType | null = null;
  private activeReactionEventId: string | null = null;

  // ── Collaborators modal ────────────────────────────────────────────────────
  showCollabModal = false;
  selectedPost: Post | null = null;

  // ── Tagged people modal ─────────────────────────────────────────────────────
  showTaggedModal = false;

  // ── Login prompt state ─────────────────────────────────────────────────────
  showLoginPrompt = false;
  loginPromptUserName = '';
  loginPromptAction = '';
  showFriendLimitPopup = false;
  friendLimitHours: number | null = null;

  // ── Post action menu (3-dot) ───────────────────────────────────────────────
  activeActionPostId: string | null = null;
  showDeletePostConfirm = false;
  private postToDelete: Post | null = null;

  // ── Report post modal ────────────────────────────────────────────────────
  isReportModalOpen = false;
  isSubmittingReport = false;
  reportError = '';
  reportSuccessOpen = false;
  private postToReport: Post | null = null;

  // ── Block user (safeword-confirm) modal ─────────────────────────────────
  showBlockUserConfirm = false;
  private postToBlock: Post | null = null;

  /** Bound to <app-block-user-confirm>'s [targetName] — "You're about to
   *  block {name}". */
  get postToBlockDisplayName(): string {
    return this.postToBlock?.hostName ?? '';
  }

  private relationshipService = inject(RelationshipService);
  private friendRequestService = inject(FriendRequestService);
  private authState = inject(AuthStateService);
  private router = inject(Router);
  private sanitizer = inject(DomSanitizer);
  private favouriteService = inject(FavouriteService);
  private editPostService = inject(EditPostService);
  private searchService = inject(SearchService);
  private feedSnapshots = inject(FeedSnapshotService);
  /** Scroll offset to return to after restoring a snapshot (see restoreSnapshot). */
  private pendingScrollY: number | null = null;
  /** Until this time, restored cards keep their saved span while media loads. */
  private keepSpansUntil = 0;
  private reportService = inject(ReportService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private postService: PostService,
    // private soundService: ReactionSoundService,
  ) { }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (this.mixedMode) {
      this.applyMixedItems();
      return;
    }

    if (!this.restoreSnapshot()) this.loadPosts();

    this.refreshSub = this.postService.onRefreshPosts$.subscribe(() => {
      this.resetAndReload();
    });

    // Keep every card by the same person in sync with friend / follow changes
    // made anywhere else (profile sidebar, another card…) — SAF-841.
    this.relationshipSub.add(this.friendRequestService.friendshipChanged$.subscribe(change => {
      let changed = false;
      for (const card of this.allPosts) {
        const matches = (change.userId && card.hostId === change.userId)
          || (!!change.requestId && card.friendshipRequestId === change.requestId);
        if (!matches || card.friendshipStatus === 'BLOCKED') continue;
        card.friendshipStatus = change.status;
        if (change.requestId !== undefined) card.friendshipRequestId = change.status === 'NONE' ? null : change.requestId;
        changed = true;
      }
      if (changed) this.cdr.detectChanges();
    }));
    this.relationshipSub.add(this.relationshipService.followChanged$.subscribe(({ userId, isFollowed }) => {
      let changed = false;
      for (const card of this.allPosts) {
        if (card.hostId === userId && card.isFollowed !== isFollowed) { card.isFollowed = isFollowed; changed = true; }
      }
      if (changed) this.cdr.detectChanges();
    }));
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Observe each card individually — only respan the card that actually changed height
    this.cardChangeSub = this.cardRefs.changes.subscribe(() => {
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          this.spanAllCards();
          this.observeAllCards();
        });
      });
    });

    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        this.spanAllCards();
        this.observeAllCards();
        if (!this.mixedMode) setTimeout(() => this.setupScrollObserver(), 50);
      });
    });

    if (this.pendingScrollY !== null) {
      // Jump before the first paint (cards already carry their saved spans),
      // so the list never shows at the top first.
      window.scrollTo({ top: this.pendingScrollY, behavior: 'instant' as ScrollBehavior });
      this.restoreScrollPosition(this.pendingScrollY);
    }

    // Keep a lightweight body observer only for window resize (column-count changes).
    // Skipped in mixedMode: on the "All" page, Posts/Blog/Events are mounted
    // together, and each respanning its cards changes document height — with
    // three document-level observers that becomes a feedback loop (each one's
    // respan re-triggers the other two), pegging the CPU and hanging the tab.
    // Per-card respanning (cardRefs.changes / onImageLoad / onVideoLoad below)
    // already keeps individual cards correctly sized without this.
    if (!this.mixedMode) {
      this.resizeObserver = new ResizeObserver(() => {
        this.zone.runOutsideAngular(() => {
          requestAnimationFrame(() => this.spanAllCards());
        });
      });
      this.resizeObserver.observe(document.documentElement);
    }
  }

  /** Attach a ResizeObserver to every card so any height change (reaction row,
   *  video load, text expand) automatically triggers a respan of just that card. */
  private observeAllCards(): void {
    if (!this.cardRefs) return;
    this.cardRefs.forEach(ref => {
      const el = ref.nativeElement;
      const elAny = el as any;
      if (elAny['__cardObserver__']) return; // already watching
      const ro = new ResizeObserver(() => {
        requestAnimationFrame(() => this.spanCard(el));
      });
      ro.observe(el);
      elAny['__cardObserver__'] = ro;
    });
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.cardChangeSub?.unsubscribe?.();
    this.refreshSub?.unsubscribe();
    this.relationshipSub.unsubscribe();
    if (this.scrollCooldownTimer) clearTimeout(this.scrollCooldownTimer);
    // Disconnect all per-card ResizeObservers
    if (this.cardRefs) {
      this.cardRefs.forEach(ref => {
        const elAny = ref.nativeElement as any;
        const ro = elAny['__cardObserver__'] as ResizeObserver | undefined;
        if (ro) { ro.disconnect(); delete elAny['__cardObserver__']; }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.mixedMode) {
      if (changes['mixedItems']) {
        this.applyMixedItems();
      }
      return;
    }
    if (changes['userName'] && !changes['userName'].firstChange) {
      this.resetAndReload();
    }
    if (changes['collabOnly'] && !changes['collabOnly'].firstChange) {
      this.resetAndReload();
    }
    if ((changes['searchQuery'] && !changes['searchQuery'].firstChange) ||
        (changes['searchHashtag'] && !changes['searchHashtag'].firstChange)) {
      this.resetAndReload();
    }
  }

  /** Renders items handed down by the "All" page mixed feed — no HTTP call,
   *  just runs them through the normal transform pipeline. */
  private applyMixedItems(): void {
    this.visiblePosts = this.transformApiPosts(this.mixedItems ?? []);
    this.totalPosts = this.visiblePosts.length;
    this.isLoading = false;
    this.allLoaded = true;
    this.cdr.detectChanges();
  }

  // ── Auth helpers ───────────────────────────────────────────────────────────

  private isLoggedIn(): boolean {
    return this.authState.getAuthSnapshot()?.isLoggedIn === true;
  }

  private openLoginPrompt(userName: string, action: string): void {
    this.loginPromptUserName = userName;
    this.loginPromptAction = action;
    this.showLoginPrompt = true;
    this.cdr.detectChanges();
  }

  closeLoginPrompt(): void {
    this.showLoginPrompt = false;
    this.cdr.detectChanges();
  }

  goToLogin(): void {
    this.closeLoginPrompt();
    this.router.navigate(['/login']);
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  navigateToPost(postId: string): void {
    this.saveSnapshot();
    this.router.navigate(['/post', postId], {
      state: { returnUrl: this.router.url }
    });
  }

  navigateToPostComments(postId: string, event: Event): void {
    event.stopPropagation();
    this.saveSnapshot();
    this.router.navigate(['/post', postId], {
      queryParams: { openComments: '1' },
      state: { returnUrl: this.router.url }
    });
  }

  /** Clicking a #hashtag chip takes the user to the "All" page, filtered to
   *  that exact hashtag across posts, blogs and events. */
  goToHashtag(tag: string, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/all'], { queryParams: { hashtag: tag } });
  }

  // ── Feed snapshot (keep place when returning from a post) ──────────────────

  /** Identifies this feed, so e.g. a profile's posts and its collab posts
   *  don't restore into each other. */
  private get snapshotKey(): string {
    return [
      'posts', this.userName ?? '', this.collabOnly, this.favouritesMode, this.featured,
      this.searchQuery ?? '', this.searchHashtag ?? '',
    ].join('|');
  }

  private saveSnapshot(): void {
    if (this.mixedMode || !isPlatformBrowser(this.platformId)) return;
    // Cards render in visiblePosts order, so index i is the same post.
    this.cardRefs?.forEach((ref, i) => {
      const post = this.visiblePosts[i];
      if (post) post.gridSpan = ref.nativeElement.style.gridRow || undefined;
    });
    this.feedSnapshots.save(this.snapshotKey, {
      url: this.router.url,
      items: this.visiblePosts,
      currentPage: this.currentPage,
      total: this.totalPosts,
      allLoaded: this.allLoaded,
      scrollY: window.scrollY,
    });
  }

  /** Puts back the pages loaded before the user opened a post. Returns false
   *  when there's nothing to restore and the feed should load normally. */
  private restoreSnapshot(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    const snap = this.feedSnapshots.take<Post>(this.snapshotKey, this.router.url);
    if (!snap || snap.items.length === 0) return false;

    this.visiblePosts = snap.items;
    this.currentPage = snap.currentPage;
    this.totalPosts = snap.total;
    this.allLoaded = snap.allLoaded;
    this.pendingScrollY = snap.scrollY;
    this.keepSpansUntil = Date.now() + 3000;
    this.isRestoring = true;
    // Don't let the sentinel fetch the next page while we scroll back down.
    this.startScrollCooldown();
    return true;
  }

  /**
   * Scrolls back to where the user was. Re-aimed every frame for a short
   * window rather than set once: the router's own scroll restoration runs
   * after us (and scrolls to the top for the in-page back button), and the
   * masonry cards/images keep growing the page for a few frames, so a single
   * scrollTo gets clamped or overridden. Stops once the position holds, or as
   * soon as the user starts scrolling themselves.
   */
  private restoreScrollPosition(targetY: number): void {
    this.pendingScrollY = null;
    const startedAt = Date.now();
    const deadline = startedAt + 2000;
    let stableFrames = 0;
    let cancelled = false;
    const cancel = () => { cancelled = true; };
    window.addEventListener('wheel', cancel, { passive: true, once: true });
    window.addEventListener('touchstart', cancel, { passive: true, once: true });
    window.addEventListener('keydown', cancel, { once: true });
    const cleanup = () => {
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
      // Keep the loader up briefly even on a fast restore so it doesn't flicker.
      const remaining = Math.max(0, 300 - (Date.now() - startedAt));
      setTimeout(() => this.zone.run(() => {
        this.isRestoring = false;
        this.cdr.markForCheck();
      }), remaining);
    };

    this.zone.runOutsideAngular(() => {
      const step = () => {
        if (cancelled || Date.now() > deadline) return cleanup();
        if (Math.abs(window.scrollY - targetY) < 2) {
          if (++stableFrames >= 5) return cleanup();
        } else {
          stableFrames = 0;
          window.scrollTo({ top: targetY, behavior: 'instant' as ScrollBehavior });
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  }

  // ── Data loading ───────────────────────────────────────────────────────────

  private loadPosts(): void {
    if (this.isLoading) return;
    this.isLoading = true;
    this.cdr.detectChanges();

    const isSearchMode = this.searchQuery !== undefined || this.searchHashtag !== undefined;

    const request$ = isSearchMode
      ? this.searchService.search(this.currentPage, PAGE_SIZE, {
          q: this.searchQuery, hashtag: this.searchHashtag, type: 'POST',
        })
      : this.favouritesMode
        ? this.favouriteService.getFavourites(this.currentPage, PAGE_SIZE, 'POST')
        : this.featured
          ? this.postService.getFeaturedPosts()
          : this.userName
            ? this.postService.getUserPosts(this.userName, this.currentPage, PAGE_SIZE, this.collabOnly)
            : this.postService.getPosts(this.currentPage, PAGE_SIZE);

    request$.subscribe({
      next: (response: any) => {
        const data: IPaginatedPosts = response.data;
        const transformedPosts = this.transformApiPosts(data.items ?? []);
        this.visiblePosts = [...this.visiblePosts, ...transformedPosts];
        this.totalPosts = data.total;
        this.isLoading = false;
        this.allLoaded = this.visiblePosts.length >= this.totalPosts;
        this.cdr.detectChanges();
        this.startScrollCooldown();
      },
      error: (err) => {
        console.error('Error loading posts:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private resetAndReload(): void {
    this.visiblePosts = [];
    this.currentPage = 1;
    this.allLoaded = false;
    this.totalPosts = 0;
    this.loadPosts();
  }

  loadNextPage(): void {
    if (this.isLoading || this.allLoaded || this.scrollCooldownActive) return;
    this.currentPage++;
    this.loadPosts();
  }

  /** See all.ts for why this cooldown exists: masonry cards use `overflow:
   *  visible`, so the grid's measured height can briefly undercount (e.g. a
   *  video whose dimensions haven't loaded yet), which can keep the scroll
   *  sentinel "intersecting" and trigger a rapid-fire fetch loop. */
  private scrollCooldownActive = false;
  private scrollCooldownTimer?: ReturnType<typeof setTimeout>;
  private startScrollCooldown(): void {
    this.scrollCooldownActive = true;
    if (this.scrollCooldownTimer) clearTimeout(this.scrollCooldownTimer);
    this.scrollCooldownTimer = setTimeout(() => { this.scrollCooldownActive = false; }, 400);
  }

  // ── Transform API data ────────────────────────────────────────────────────

  private transformApiPosts(apiPosts: IPost[]): Post[] {
    return apiPosts?.map(apiPost => this.transformPost(apiPost));
  }

  private transformPost(apiPost: IPost): Post {
    const hasBackground = !!(apiPost.postInfo?.backgroundPattern && apiPost.postInfo?.backgroundBg);

    const acceptedCollaborators = (apiPost.postInfo?.collaborations || [])
      .filter((collab: any) => collab.status === 'ACCEPTED')
      .map((collab: any) => {
        const user = collab.user;
        return {
          _id: user._id || user,
          displayName: user.displayName || '',
          userName: user.userName || '',
          profileImage: user.profileImage || undefined,
        };
      });

    return {
      id: apiPost._id,
      hostName: apiPost.user.userName,
      hostAvatar: apiPost.user.profileImage?.urlSmall || apiPost.user.profileImage?.urlMedium || '',
      hostId: apiPost?.user?._id ?? '',
      timeAgo: this.getTimeAgo(apiPost.createdAt),
      images: apiPost.media.map(m => ({
        url: m.type === 'video' ? m.url : (m.urlLarge || m.urlMedium || m.urlSmall || ''),
        type: m.type
      })),
      rawMedia: apiPost.media.map((m: any) => ({
        _id: m._id ?? '',
        url: m.url ?? '',
        urlLarge: m.urlLarge,
        urlMedium: m.urlMedium,
        urlSmall: m.urlSmall,
        type: (m.type ?? 'image') as 'image' | 'video',
      })),
      activeSlide: 0,
      title: apiPost.title || '',
      description: apiPost.title || '',
      expanded: false,
      myReaction: apiPost?.reactions?.myReaction ?? null,
      reactions: apiPost.reactions.total,
      comments: apiPost.numberOfComments,
      shares: apiPost.numberOfShares,
      isMyPost: apiPost.isMyPost,
      isFollowed: apiPost.isFollowed,
      totalReactions: apiPost.reactions?.total ?? 0,
      likes: apiPost?.reactions?.total ?? apiPost?.numberOfLikes ?? 0,
      friendshipStatus: (apiPost?.friendship?.status ?? 'NONE') as FriendshipStatus,
      location: apiPost.postInfo?.location,
      hashtags: apiPost.hashtags || [],
      commentsDisabled: apiPost.postInfo?.commentsDisabled ?? false,
      backgroundPattern: apiPost.postInfo?.backgroundPattern,
      backgroundBg: apiPost.postInfo?.backgroundBg,
      showEmojiPicker: false,
      mentions: apiPost.postInfo?.mentions || [],
      hoveredReaction: null,
      collaborations: apiPost.postInfo?.collaborations || [],
      hasBackground: hasBackground,
      friendshipRequestId: apiPost?.friendship?.requestId ?? null,
      isFavourite: (apiPost as any).isFavourite ?? false,
      globalOrder: (apiPost as any)._order,
      acceptedCollaborators: acceptedCollaborators,
      reactionCounts: apiPost.reactions?.counts ?? { LIKE: 0, HEART: 0, HUG: 0, WOW: 0, SAD: 0, HAHA: 0 },
      recentUserName: apiPost.reactions?.recentUserName ?? null,
      topTypes: (apiPost.reactions?.topTypes ?? []) as ReactionType[],
      isRemovedByAdmin: (apiPost as any).isRemovedByAdmin ?? false,
    };
  }

  private getTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds} sec ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hour${Math.floor(seconds / 3600) > 1 ? 's' : ''} ago`;
    if (seconds < 172800) return 'Yesterday';
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    if (seconds < 2592000) return `${Math.floor(seconds / 604800)} week${Math.floor(seconds / 604800) > 1 ? 's' : ''} ago`;
    return `${Math.floor(seconds / 2592000)} month${Math.floor(seconds / 2592000) > 1 ? 's' : ''} ago`;
  }

  trackById(_: number, post: Post): string { return post.id; }

  /** Single source of truth for the ui-avatars fallback used across this page. */
  avatarFallback(name?: string | null): string {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFD4E0&color=D31148`;
  }

  // ── Grid span ──────────────────────────────────────────────────────────────

  private spanAllCards(): void {
    if (!this.cardRefs) return;
    this.cardRefs.forEach(ref => this.spanCard(ref.nativeElement));
  }

  private spanCard(card: HTMLElement): void {
    // A restored card already has its real span; measuring it before its
    // media has loaded would shrink it and then grow it back (a visible
    // blink). Keep the span until onImageLoad/onVideoLoad respans it.
    if (Date.now() < this.keepSpansUntil && card.style.gridRow && this.hasPendingMedia(card)) return;
    card.style.gridRow = '';
    if (this.uniform) return; // equal-height grid: no masonry row spans
    const height = card.getBoundingClientRect().height;
    const span = Math.ceil((height + ROW_GAP) / ROW_UNIT);
    card.style.gridRow = `span ${span}`;
  }

  private hasPendingMedia(card: HTMLElement): boolean {
    const imgs = Array.from(card.querySelectorAll('img'));
    const videos = Array.from(card.querySelectorAll('video'));
    return imgs.some(img => !img.complete) || videos.some(v => v.readyState < 1);
  }

  // ── Scroll observer ────────────────────────────────────────────────────────

  private setupScrollObserver(): void {
    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isLoading && !this.allLoaded) {
          this.zone.run(() => this.loadNextPage());
        }
      },
      { threshold: 0 },
    );
    this.scrollObserver.observe(this.sentinelRef.nativeElement);
  }

  // ── Image / video load ─────────────────────────────────────────────────────

  onImageLoad(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const img = event.target as HTMLImageElement;
    const card = img.closest<HTMLElement>('.masonry-item');
    if (!card) return;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.spanCard(card));
    });
  }

  onVideoLoad(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const video = event.target as HTMLVideoElement;
    const card = video.closest<HTMLElement>('.masonry-item');
    if (!card) return;
    // Double rAF — first frame lets the video resize, second measures correctly
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.spanCard(card)));
    });
  }

  /** Only one video should play at a time — pause every other playing video when this one starts. */
  onVideoPlay(event: Event): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const current = event.target as HTMLVideoElement;
    document.querySelectorAll<HTMLVideoElement>('video').forEach((v) => {
      if (v !== current && !v.paused) v.pause();
    });
  }

  /** Re-measure a specific card after a DOM change (e.g. reaction row appearing).
   *  Uses a double rAF so Angular's change detection has fully applied before we measure. */
  private respanCard(card: HTMLElement): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => this.spanCard(card)));
    });
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  public follow(event: MouseEvent, card: Post): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.hostName, 'follow');
      return;
    }
    card.isFollowed = true;
    this.cdr.detectChanges();
    this.relationshipService.follow(card.hostId).subscribe({
      error: () => { card.isFollowed = false; this.cdr.detectChanges(); },
    });
  }

  public unfollow(event: MouseEvent, card: Post): void {
    event.stopPropagation();
    this.activeActionPostId = null;
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.hostName, 'unfollow');
      return;
    }
    card.isFollowed = false;
    this.cdr.detectChanges();
    this.relationshipService.unfollow(card.hostId).subscribe({
      error: () => { card.isFollowed = true; this.cdr.detectChanges(); },
    });
  }

  expandPost(post: Post): void {
    post.expanded = true;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.spanAllCards());
    });
  }

  collapsePost(post: Post): void {
    post.expanded = false;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.spanAllCards());
    });
  }

  prevSlide(post: Post, event: Event): void {
    event.stopPropagation();
    post.activeSlide = post.activeSlide === 0 ? post.images.length - 1 : post.activeSlide - 1;
    this.respanAfterSlide(event);
  }

  nextSlide(post: Post, event: Event): void {
    event.stopPropagation();
    post.activeSlide = post.activeSlide === post.images.length - 1 ? 0 : post.activeSlide + 1;
    this.respanAfterSlide(event);
  }

  goToSlide(post: Post, index: number, event: Event): void {
    event.stopPropagation();
    post.activeSlide = index;
    this.respanAfterSlide(event);
  }

  private respanAfterSlide(event: Event): void {
    const card = (event.target as HTMLElement).closest<HTMLElement>('.masonry-item');
    if (!card) return;
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => { requestAnimationFrame(() => { this.spanCard(card); }); });
    });
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const activeImg = card.querySelector<HTMLImageElement>('img.block, img:not(.hidden)');
        if (activeImg && !activeImg.complete) {
          const handler = () => {
            requestAnimationFrame(() => this.spanCard(card));
            activeImg.removeEventListener('load', handler);
          };
          activeImg.addEventListener('load', handler);
        }
      });
    });
  }

  // ── Pattern & caption helpers ──────────────────────────────────────────────

  getPatternClass(patternId?: string): string {
    if (!patternId) return '';
    const patternMap: Record<string, string> = {
      'p-checks-purple': 'pattern-checks-md',
      'p-zigzag-sky': 'pattern-zigzag-md',
      'p-grid-green': 'pattern-grid-md',
      'p-diag-orange': 'pattern-diagonal-lines-md',
      'p-hlines-teal': 'pattern-horizontal-lines-md',
      'p-vstripe-rose': 'pattern-vertical-stripes-md',
      'p-crossdots-navy': 'pattern-cross-dots-lg',
      'p-dstripe-amber': 'pattern-diagonal-stripes-md',
      'p-h-indigo': 'pattern-h-lg',
    };
    return patternMap[patternId] || '';
  }

  getHighlightedCaption(text: string): any {
    if (!text) return '';
    const escaped = text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    const highlighted = escaped.replace(
      /@((?:\w+\.)*\w+)/g,
      '<span class="mention-link" data-username="$1" style="color: #2563eb; font-weight: 700; cursor: pointer;">@$1</span>'
    );
    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  onCaptionClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('mention-link')) {
      event.stopPropagation();
      const username = target.getAttribute('data-username');
      if (username) this.router.navigate(['/profile', username, 'post']);
    }
  }

  encodeUri(str: string): string { return encodeURIComponent(str); }

  // ── Share ────────────────────────────────────────────────────────────────────
  activeSharePost: ShareConfig | null = null;
  activeShareCount = 0;
  private activeSharePostRef: any = null;

  openShareSheet(post: any, event: MouseEvent): void {
    event.stopPropagation();
    this.activeSharePostRef = post;
    this.activeShareCount = post.shares ?? 0;
    this.activeSharePost = {
      contentId: post.id,
      contentType: 'post',
      title: post.text?.slice(0, 80) ?? 'Check out this post',
      description: post.text?.slice(0, 160),
      imageUrl: post.media?.[0]?.url,
    };
    this.cdr.detectChanges();
  }

  closeShareSheet(): void {
    this.activeSharePost = null;
    this.activeSharePostRef = null;
    this.cdr.detectChanges();
  }

  onShareCountUpdated(count: number): void {
    if (this.activeSharePostRef) {
      this.activeSharePostRef.shares = count;
    }
    this.cdr.detectChanges();
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  toggleFavourite(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(post.hostName, 'save to favourites');
      return;
    }
    const prev = post.isFavourite;
    post.isFavourite = !prev;
    this.cdr.detectChanges();
    this.favouriteService.toggle(post.id).subscribe({
      next: (res: any) => {
        post.isFavourite = res?.data?.isFavourite ?? !prev;
        this.favouriteService.broadcastToggle(post.id, post.isFavourite);
        this.cdr.detectChanges();
      },
      error: () => { post.isFavourite = prev; this.cdr.detectChanges(); },
    });
  }

  // ── Collaborators modal ────────────────────────────────────────────────────

  showCollaboratorsModal(post: Post): void {
    this.selectedPost = post;
    this.showCollabModal = true;
  }

  closeCollaboratorsModal(): void {
    this.showCollabModal = false;
    this.selectedPost = null;
  }

  // ── Tagged people modal ─────────────────────────────────────────────────────

  showTaggedPeopleModal(post: Post, event: Event): void {
    event.stopPropagation();
    this.selectedPost = post;
    this.showTaggedModal = true;
  }

  closeTaggedPeopleModal(): void {
    this.showTaggedModal = false;
    this.selectedPost = null;
  }

  // ── Reactions ──────────────────────────────────────────────────────────────

  getReactionConfig(type: ReactionType | null): typeof REACTIONS[0] | null {
    if (!type) return null;
    return REACTIONS.find(r => r.type === type) ?? null;
  }

  onReactionClick(card: any): void {
    if (!this.isLoggedIn()) {
      const collaboratorNames = (card.acceptedCollaborators ?? [])
        .map((c: any) => c.userName)
        .join(', ');
      const displayName = collaboratorNames
        ? `${card.hostName} & ${collaboratorNames}`
        : card.hostName;
      this.openLoginPrompt(displayName, 'react to this post by');
      return;
    }

    if (card.showEmojiPicker) {
      card.showEmojiPicker = false;
    } else {
      // this.soundService.playPickerOpen();
      this.allPosts.forEach(e => e.showEmojiPicker = false);
      card.showEmojiPicker = true;
    }
    this.cdr.detectChanges();
  }

  onEmojiPick(card: Post, type: ReactionType, $event: MouseEvent): void {
    $event.stopPropagation();
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    // this.soundService.playReactionSelect(type);
    this.submitReaction(card, type);
  }

  private submitReaction(card: Post, type: ReactionType): void {
    const previous = card.myReaction;
    const previousCounts = { ...card.reactionCounts };
    const previousTotal = card.totalReactions;
    const previousReactions = card.reactions;
    const previousTopTypes = [...card.topTypes];
    const previousRecentUserName = card.recentUserName;
    const wasZero = card.totalReactions === 0;

    if (previous === type) {
      card.reactionCounts[type] = Math.max(0, card.reactionCounts[type] - 1);
      card.totalReactions = Math.max(0, card.totalReactions - 1);
      card.myReaction = null;
    } else {
      if (previous) {
        card.reactionCounts[previous as ReactionType] = Math.max(0, card.reactionCounts[previous as ReactionType] - 1);
        card.totalReactions = Math.max(0, card.totalReactions - 1);
      }
      card.reactionCounts[type]++;
      card.totalReactions++;
      card.myReaction = type;
    }
    card.reactions = card.totalReactions;
    card.likes = card.totalReactions;

    // Update topTypes and recentUserName immediately so the UI reflects the change
    card.topTypes = this.computeTopTypes(card.reactionCounts);
    const currentUserName = this.authState.getAuthSnapshot()?.user?.userName ?? null;
    if (card.myReaction) {
      card.recentUserName = currentUserName;
    } else if (card.totalReactions === 0) {
      card.recentUserName = null;
    }

    this.cdr.detectChanges();

    // If the reaction row just appeared (was 0 → now > 0) or disappeared,
    // the card height changed — re-measure after Angular has rendered
    if (wasZero !== (card.totalReactions === 0)) {
      const idx = this.visiblePosts.indexOf(card);
      const cardEl = idx >= 0 ? this.cardRefs?.toArray()[idx]?.nativeElement ?? null : null;
      if (cardEl) {
        this.respanCard(cardEl);
      } else {
        this.zone.runOutsideAngular(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => this.spanAllCards()));
        });
      }
    }

    this.postService.reactToPost(card.id, type).subscribe({
      error: () => {
        card.myReaction = previous;
        card.reactionCounts = previousCounts;
        card.totalReactions = previousTotal;
        card.reactions = previousReactions;
        card.likes = previousTotal;
        card.topTypes = previousTopTypes;
        card.recentUserName = previousRecentUserName;
        this.cdr.detectChanges();
        this.zone.runOutsideAngular(() => {
          requestAnimationFrame(() => requestAnimationFrame(() => this.spanAllCards()));
        });
      }
    });
  }

  private computeTopTypes(counts: ReactionCounts): ReactionType[] {
    return (Object.entries(counts) as [ReactionType, number][])
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([type]) => type);
  }

  openReactionModal(card: Post): void {
    this.isReactionOpen = true;
    this.activeReactionFilter = null;
    this.activeReactionEventId = card.id;
    this.loadReactions(card.id);
  }

  closeReactionModal(): void {
    this.isReactionOpen = false;
    this.reactionData = null;
    this.activeReactionEventId = null;
  }

  closeEmojiPicker(card: Post): void {
    card.showEmojiPicker = false;
    card.hoveredReaction = null;
    this.cdr.detectChanges();
  }

  setReactionFilter(type: ReactionType | null): void {
    this.activeReactionFilter = type;
    if (this.activeReactionEventId) {
      this.loadReactions(this.activeReactionEventId, type ?? undefined);
    }
  }

  private loadReactions(eventId: string, type?: ReactionType): void {
    this.reactionsLoading = true;
    this.cdr.detectChanges();
    this.postService.getReactions(eventId, type).subscribe({
      next: (res: any) => {
        this.reactionData = res?.data;
        this.reactionsLoading = false;
        this.cdr.detectChanges();
      },
      error: () => { this.reactionsLoading = false; this.cdr.detectChanges(); }
    });
  }

  get filteredReactions(): any[] { return this.reactionData?.all ?? []; }

  // ── Friend helpers ─────────────────────────────────────────────────────────

  friendButtonTitle(status: FriendshipStatus): string {
    const map: Record<FriendshipStatus, string> = {
      NONE: 'Send friend request',
      PENDING_SENT: 'Cancel friend request',
      PENDING_RECEIVED: 'Accept friend request',
      FRIENDS: 'Unfriend',
      BLOCKED: 'Blocked',
    };
    return map[status];
  }

  onFriendButtonClick(card: Post, event: MouseEvent): void {
    event.stopPropagation();
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(card.hostName, 'add as a friend');
      return;
    }
    switch (card.friendshipStatus) {
      case 'NONE': this.sendFriendRequest(card, event); break;
      case 'PENDING_SENT': this.cancelFriendRequest(card, event); break;
      case 'PENDING_RECEIVED': this.acceptFriendRequest(card, event); break;
      case 'FRIENDS': this.promptUnfriend(card, event); break;
      case 'BLOCKED': break;
    }
  }

  showUnfriendConfirm = false;
  private pendingUnfriendAction: (() => void) | null = null;

  private promptUnfriend(card: Post, event: MouseEvent): void {
    event.stopPropagation();
    this.pendingUnfriendAction = () => this.unfriend(card, event);
    this.showUnfriendConfirm = true;
  }

  confirmUnfriend(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction?.();
    this.pendingUnfriendAction = null;
  }

  cancelUnfriendConfirm(): void {
    this.showUnfriendConfirm = false;
    this.pendingUnfriendAction = null;
  }

  /** They've sent you a request — clicking the friend icon accepts it. (It
   *  used to call sendFriendRequest, which bails unless the status is NONE,
   *  so the button did nothing — SAF-839.) */
  private acceptFriendRequest(card: Post, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'PENDING_RECEIVED' || !card.friendshipRequestId) return;
    card.friendshipStatus = 'FRIENDS';
    this.cdr.detectChanges();
    this.friendRequestService.accept(card.friendshipRequestId, card.hostId).subscribe({
      error: () => { card.friendshipStatus = 'PENDING_RECEIVED'; this.cdr.detectChanges(); },
    });
  }

  private sendFriendRequest(card: Post, event: MouseEvent): void {
    event.stopPropagation();
    if (card.friendshipStatus !== 'NONE') return;
    card.friendshipStatus = 'PENDING_SENT';
    this.cdr.detectChanges();
    this.friendRequestService.send(card.hostId).subscribe({
      next: (res: any) => { card.friendshipRequestId = res?.data?._id ?? null; this.cdr.detectChanges(); },
      error: (err) => {
        const status = err?.error?.statusCode ?? err?.status;
        if (status !== 409) card.friendshipStatus = 'NONE';
        const cooldownHours = this.friendRequestService.parseCooldownError(err);
        if (cooldownHours !== null) {
          this.friendLimitHours = cooldownHours;
          this.showFriendLimitPopup = true;
        }
        this.cdr.detectChanges();
      },
    });
  }

  private cancelFriendRequest(card: Post, event: MouseEvent): void {
    event.stopPropagation();
    if (!card.friendshipRequestId) return;
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    this.cdr.detectChanges();
    this.friendRequestService.cancel(card.friendshipRequestId, card.hostId).subscribe({
      error: () => { card.friendshipStatus = prevStatus; card.friendshipRequestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  private unfriend(card: Post, event: MouseEvent): void {
    event.stopPropagation();
    const prevStatus = card.friendshipStatus;
    const prevRequestId = card.friendshipRequestId;
    card.friendshipStatus = 'NONE';
    card.friendshipRequestId = null;
    this.cdr.detectChanges();
    this.friendRequestService.unfriend(card.hostId).subscribe({
      error: () => { card.friendshipStatus = prevStatus; card.friendshipRequestId = prevRequestId; this.cdr.detectChanges(); },
    });
  }

  onModalFollowClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'follow');
      return;
    }
    event.stopPropagation();
    if (item.isFollowed) {
      item.isFollowed = false;
      this.cdr.markForCheck();
      this.relationshipService.unfollow(item.user?._id).subscribe({
        error: () => { item.isFollowed = true; this.cdr.markForCheck(); }
      });
    } else {
      item.isFollowed = true;
      this.cdr.markForCheck();
      this.relationshipService.follow(item.user?._id).subscribe({
        error: () => { item.isFollowed = false; this.cdr.markForCheck(); }
      });
    }
  }

  onModalFriendClick(item: any, event: MouseEvent): void {
    if (!this.isLoggedIn()) {
      this.openLoginPrompt(item.user?.userName ?? item.user?.userName, 'add as a friend');
      return;
    }
    event.stopPropagation();
    const status = item.friendship.status ?? 'NONE';
    if (status === 'NONE') {
      item.friendship.status = 'PENDING_SENT';
      this.cdr.markForCheck();
      this.friendRequestService.send(item.user?._id).subscribe({
        next: (res: any) => { item.friendship.requestId = res?.data?._id ?? null; this.cdr.markForCheck(); },
        error: (err: any) => {
          const code = err?.error?.statusCode ?? err?.status;
          if (code !== 409) item.friendship.status = 'NONE';
          const cooldownHours = this.friendRequestService.parseCooldownError(err);
          if (cooldownHours !== null) {
            this.friendLimitHours = cooldownHours;
            this.showFriendLimitPopup = true;
          }
          this.cdr.markForCheck();
        }
      });
    } else if (status === 'PENDING_SENT') {
      const prev = item.friendship.status;
      const prevId = item.friendship.requestId;
      item.friendship.status = 'NONE';
      this.cdr.markForCheck();
      this.friendRequestService.cancel(item.friendship.requestId, item.user?._id).subscribe({
        error: () => { item.friendship.status = prev; item.friendship.requestId = prevId; this.cdr.markForCheck(); }
      });
    } else if (status === 'FRIENDS') {
      this.pendingUnfriendAction = () => {
        const prev = item.friendship.status;
        item.friendship.status = 'NONE';
        this.cdr.markForCheck();
        this.friendRequestService.unfriend(item.user?._id).subscribe({
          error: () => { item.friendship.status = prev; this.cdr.markForCheck(); }
        });
      };
      this.showUnfriendConfirm = true;
    }
  }
  // ── Touch swipe ────────────────────────────────────────────────────────────

  /** Touch swipe on the card carousel. `dir` is +1 for next, -1 for previous. */
  onSlideSwipe(post: Post, dir: 1 | -1, host: HTMLElement): void {
    const total = post.images.length;
    if (total <= 1) return;
    post.activeSlide = (post.activeSlide + dir + total) % total;
    this.cdr.detectChanges();
    const card = host.closest<HTMLElement>('.masonry-item');
    if (card) this.respanAfterSlideCard(card);
  }

  private respanAfterSlideCard(card: HTMLElement): void {
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => { requestAnimationFrame(() => { this.spanCard(card); }); });
    });
    this.zone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        const activeImg = card.querySelector<HTMLImageElement>('img.block, img:not(.hidden)');
        if (activeImg && !activeImg.complete) {
          const handler = () => {
            requestAnimationFrame(() => this.spanCard(card));
            activeImg.removeEventListener('load', handler);
          };
          activeImg.addEventListener('load', handler);
        }
      });
    });
  }

  // ── Post action menu (3-dot) ───────────────────────────────────────────────

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.activeActionPostId !== null) {
      this.activeActionPostId = null;
      this.cdr.detectChanges();
    }
  }

  togglePostActionMenu(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionPostId = this.activeActionPostId === post.id ? null : post.id;
    this.cdr.detectChanges();
  }

  /** Post whose comments on/off request is in flight (disables its menu item). */
  togglingCommentsId: string | null = null;

  /** Turn comments on/off from the card's 3-dot menu — same API as post-details. */
  toggleComments(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    if (this.togglingCommentsId) return;
    this.togglingCommentsId = post.id;
    this.postService.toggleComments(post.id).subscribe({
      next: (res: any) => {
        post.commentsDisabled = res.data?.postInfo?.commentsDisabled ?? !post.commentsDisabled;
        this.togglingCommentsId = null;
        this.activeActionPostId = null;
        this.cdr.detectChanges();
      },
      error: () => {
        this.togglingCommentsId = null;
        this.cdr.detectChanges();
      },
    });
  }

  editPost(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionPostId = null;

    // Build collaborators from raw collaborations (same as post-details)
    const rawCollabs: any[] = post.collaborations ?? [];
    const collaborators: IEditPostUser[] = rawCollabs
      .filter((c: any) => c.user && typeof c.user === 'object')
      .map((c: any) => ({
        _id: c.user._id ?? c.user.id ?? '',
        displayName: c.user.displayName ?? '',
        userName: c.user.userName ?? '',
        profileImage: c.user.profileImage ?? null,
        collabStatus: (c.status ?? 'PENDING') as 'ACCEPTED' | 'PENDING' | 'DECLINED',
      }));

    // Tagged people from mentions (same as post-details)
    const rawMentions: any[] = post.mentions ?? [];
    const taggedPeople: IEditPostUser[] = rawMentions
      .filter((u: any) => u && typeof u === 'object' && u._id)
      .map((u: any) => ({
        _id: u._id ?? '',
        displayName: u.displayName ?? '',
        userName: u.userName ?? '',
        profileImage: u.profileImage ?? null,
      }));

    const editRequest: IEditPostRequest = {
      postId: post.id,
      caption: post.description || post.title || '',
      backgroundPattern: post.backgroundPattern || undefined,
      backgroundBg: post.backgroundBg || undefined,
      location: post.location || undefined,
      hashtags: post.hashtags || [],
      // Use rawMedia so real _ids are preserved — prevents media being deleted on save
      existingMedia: post.rawMedia.map((m) => ({
        _id: m._id,
        url: m.urlLarge || m.urlMedium || m.url || '',
        urlLarge: m.urlLarge,
        urlMedium: m.urlMedium,
        type: m.type,
      })),
      taggedPeople,
      collaborators,
    };
    setTimeout(() => this.editPostService.openEditModal(editRequest), 200);
    this.cdr.detectChanges();
  }

  confirmDeletePost(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionPostId = null;
    this.postToDelete = post;
    this.showDeletePostConfirm = true;
    this.cdr.detectChanges();
  }

  cancelDeletePost(): void {
    this.showDeletePostConfirm = false;
    this.postToDelete = null;
    this.cdr.detectChanges();
  }

  doDeletePost(): void {
    if (!this.postToDelete) return;
    const post = this.postToDelete;
    this.showDeletePostConfirm = false;
    this.postToDelete = null;
    this.postService.deletePost(post.id).subscribe({
      next: () => {
        this.visiblePosts = this.visiblePosts.filter(p => p.id !== post.id);
        this.postService.triggerRefresh();
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error deleting post:', err);
        this.cdr.detectChanges();
      }
    });
  }

  reportPost(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionPostId = null;
    this.postToReport = post;
    this.reportError = '';
    this.isReportModalOpen = true;
    this.cdr.detectChanges();
  }

  closeReportModal(): void {
    if (this.isSubmittingReport) return;
    this.isReportModalOpen = false;
    this.postToReport = null;
    this.reportError = '';
    this.cdr.detectChanges();
  }

  submitPostReport(description: string): void {
    if (!this.postToReport) return;
    this.isSubmittingReport = true;
    this.reportError = '';

    this.reportService.submitReport(this.postToReport.id, description).subscribe({
      next: () => {
        this.zone.run(() => {
          this.isSubmittingReport = false;
          this.isReportModalOpen = false;
          this.postToReport = null;
          this.reportSuccessOpen = true;
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        this.zone.run(() => {
          this.isSubmittingReport = false;
          this.reportError = err?.error?.message ?? 'Failed to submit report. Please try again.';
          this.cdr.detectChanges();
        });
      },
    });
  }

  closeReportSuccess(): void {
    this.reportSuccessOpen = false;
    this.cdr.detectChanges();
  }

  // ── Block user (from a post's 3-dot menu) ───────────────────────────────

  blockPostUser(post: Post, event: MouseEvent): void {
    event.stopPropagation();
    this.activeActionPostId = null;
    this.postToBlock = post;
    this.showBlockUserConfirm = true;
    this.cdr.detectChanges();
  }

  cancelBlockPostUser(): void {
    this.showBlockUserConfirm = false;
    this.postToBlock = null;
    this.cdr.detectChanges();
  }

  confirmBlockPostUser(): void {
    this.showBlockUserConfirm = false;
    const post = this.postToBlock;
    this.postToBlock = null;
    if (!post?.hostId) return;

    // Optimistically drop every post from this author out of the current
    // list — mirrors what a fresh feed fetch would return once blocked
    // users' content is filtered out server-side.
    const prevPosts = this.visiblePosts;
    this.visiblePosts = this.visiblePosts.filter(p => p.hostId !== post.hostId);
    this.cdr.detectChanges();

    this.friendRequestService.block(post.hostId).subscribe({
      next: () => this.friendRequestService.triggerRefresh(),
      error: (err) => {
        console.error('Block user failed:', err);
        this.visiblePosts = prevPosts;
        this.cdr.detectChanges();
      },
    });
  }
}